import { cn, formatCharCount } from '@/lib/utils';
import { Copy, Heart, Trash2, Check, ChevronDown, ThumbsUp, ThumbsDown, Pencil, X, Eye, EyeOff } from 'lucide-react';
import MarkdownPreview from './MarkdownPreview';
import { format } from 'date-fns';
import { useSelector } from '@legendapp/state/react';
import { ui$, toggleSnippetSelection } from '@/stores/ui$';
import { updateNote } from '@/stores/notes$';
import { snippets$, toggleFavorite, togglePreviewMode, deleteSnippet, upvoteSnippet, downvoteSnippet, updateSnippet } from '@/stores/snippets$';
import { useState, useEffect, useRef } from 'react';
import ImagePreview from './ImagePreview';
import ImageLightbox from './ImageLightbox';
import { useImageUpload } from '@/hooks/useImageUpload';
import { auth$ } from '@/stores/auth$';
import { trackEvent } from '@/lib/analytics';

export default function SnippetItem({ snippet, noteId }) {
    const selectedSnippetIds = useSelector(() => ui$.selectedSnippetIds.get());
    const user = useSelector(() => auth$.user.get());

    const [isExpanded, setIsExpanded] = useState(false);
    const [showStickyCollapse, setShowStickyCollapse] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editContent, setEditContent] = useState('');
    const [editImages, setEditImages] = useState([]); // URLs of existing images in edit mode
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const [isPreview, setIsPreview] = useState(snippet.preview_mode ?? false);
    const isSelected = selectedSnippetIds.includes(snippet.id);
    const snippetRef = useRef(null);
    const copyTimeoutRef = useRef(null);
    const contentTextareaRef = useRef(null);

    // Image upload hook for edit mode
    const {
        pendingImages,
        addImages,
        removeImage: removePendingImage,
        clearImages,
        uploadNewImages,
        canAddMore,
    } = useImageUpload();

    // Snippet images (from database)
    const snippetImages = snippet.images || [];

    // Combined images for edit mode (existing + pending)
    const totalEditImages = editImages.length + pendingImages.length;

    // Calculate opacity based on votes (0.4 for downvoted)
    const getOpacity = () => {
        if (snippet.downvotes > 0) return 0.4;
        return 1;
    };

    // Check if content exceeds max height (200px ≈ 10 lines at 1.5 line-height)
    const needsExpand = snippet.content.split('\n').length > 10 || snippet.content.length > 500;

    // Handle scroll detection for sticky "Show less" button
    useEffect(() => {
        if (!isExpanded || !needsExpand) {
            setShowStickyCollapse(false);
            return;
        }

        const handleScroll = () => {
            if (!snippetRef.current) return;

            const rect = snippetRef.current.getBoundingClientRect();
            const snippetBottom = rect.bottom;
            const viewportHeight = window.innerHeight;

            // Show sticky button if snippet extends below viewport
            setShowStickyCollapse(snippetBottom > viewportHeight - 100);
        };

        handleScroll(); // Check on expand
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', handleScroll);
        };
    }, [isExpanded, needsExpand]);

    // Cleanup timeout on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    // Auto-grow textarea based on content
    useEffect(() => {
        if (isEditing && contentTextareaRef.current) {
            const lineCount = editContent.split('\n').length;
            const calculatedRows = Math.min(Math.max(lineCount, 5), 20);
            contentTextareaRef.current.rows = calculatedRows;
        }
    }, [editContent, isEditing]);

    const copyToClipboard = () => {
        navigator.clipboard.writeText(snippet.content);
        setIsCopied(true);
        trackEvent('snippet_copied', { content_length: snippet.content?.length || 0 });

        // Increment copy count (local-first, syncs via Legend-State)
        snippets$[snippet.id].copy_count.set((snippet.copy_count || 0) + 1);

        // Clear any existing timeout
        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
        }

        // Reset after 2 seconds
        copyTimeoutRef.current = setTimeout(() => {
            setIsCopied(false);
        }, 2000);
    };

    const handleEdit = () => {
        setEditTitle(snippet.title || '');
        setEditContent(snippet.content);
        setEditImages([...snippetImages]); // Copy existing images
        clearImages(); // Clear any pending images from previous edits
        setIsEditing(true);
        // Focus textarea after render
        setTimeout(() => {
            if (contentTextareaRef.current) {
                contentTextareaRef.current.focus();
                // Move cursor to end
                contentTextareaRef.current.setSelectionRange(
                    contentTextareaRef.current.value.length,
                    contentTextareaRef.current.value.length
                );
            }
        }, 0);
    };

    const handleSave = async () => {
        // Upload any new pending images and combine with existing
        let finalImages = [...editImages];

        if (pendingImages.length > 0 && user) {
            try {
                const newUrls = await uploadNewImages(user.id, snippet.id, []);
                finalImages = [...editImages, ...newUrls];
            } catch (err) {
                console.error('[SnippetItem] Failed to upload images:', err);
                return; // Don't save if upload fails
            }
        }

        // Update snippet with new values including images
        updateSnippet(snippet.id, {
            title: editTitle,
            content: editContent,
            images: finalImages,
        }, (noteId) => {
            updateNote(noteId, { updated_at: new Date().toISOString() });
        });
        setIsEditing(false);
        clearImages();
    };

    const handleCancel = () => {
        // Check if there are unsaved changes
        const hasContentChanges = editTitle !== (snippet.title || '') || editContent !== snippet.content;
        const hasImageChanges = JSON.stringify(editImages) !== JSON.stringify(snippetImages) || pendingImages.length > 0;

        if (hasContentChanges || hasImageChanges) {
            const confirmCancel = window.confirm('You have unsaved changes. Are you sure you want to cancel?');
            if (!confirmCancel) return;
        }

        setIsEditing(false);
        clearImages();
    };

    // Handle removing an existing image in edit mode
    const handleRemoveEditImage = (indexOrId) => {
        if (typeof indexOrId === 'number') {
            // It's an index from existing images
            setEditImages(prev => prev.filter((_, i) => i !== indexOrId));
        } else {
            // It's an id from pending images
            removePendingImage(indexOrId);
        }
    };

    // Open lightbox at specific image
    const handleImageClick = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };

    // Keyboard shortcuts for edit mode
    const handleKeyDown = (e) => {
        if (!isEditing) return;

        // ESC to cancel
        if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }

        // Cmd+Enter or Ctrl+Enter to save
        if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        }
    };

    return (
        <div
            ref={snippetRef}
            className={cn("group relative pl-8 pb-8 transition-opacity duration-300", isSelected && "opacity-100")}
            style={{ opacity: getOpacity() }}
            onKeyDown={handleKeyDown}
            data-testid="snippet-item"
        >
             {/* Timeline Line */}
             <div className="absolute left-[11px] top-8 bottom-0 w-px bg-border group-last:hidden"></div>

             {/* Timeline Node - Version Number OR Checkbox */}
             <div className="absolute left-0 top-0 flex flex-col items-center group-hover:z-20">
                 <button
                    onClick={() => toggleSnippetSelection(snippet.id)}
                    className={cn(
                        "h-6 w-6 rounded-full border flex items-center justify-center text-[10px] font-mono transition-all z-10",
                        isSelected
                            ? "bg-primary border-primary text-primary-foreground"
                            : "bg-muted border-border text-muted-foreground group-hover:bg-background group-hover:border-primary/50 group-hover:text-primary cursor-pointer"
                    )}
                 >
                     {isSelected ? <Check size={12} /> : (
                         <span className="group-hover:hidden">v{snippet.version}</span>
                     )}
                     <div className={cn("hidden", !isSelected && "group-hover:block", "h-2.5 w-2.5 rounded-[1px] border border-primary/50 bg-transparent")} />
                 </button>
             </div>

             <div className={cn(
                 "bg-card border rounded-lg shadow-sm overflow-hidden transition-all",
                 isSelected ? "border-primary ring-1 ring-primary/20 shadow-md" :
                 snippet.upvotes > 0 ? "border-green-600 dark:border-green-500" :
                 "border-border",
                 isEditing && "bg-primary/5 border-primary/30"
             )}>
                 {/* Header */}
                 <div className={cn("flex items-center justify-between px-3 py-2 border-b transition-colors", isSelected ? "bg-primary/5 border-primary/10" : "bg-muted/30 border-border")}>
                     <div className="flex items-center gap-2 flex-1">
                         {isEditing ? (
                             <input
                                 type="text"
                                 value={editTitle}
                                 onChange={(e) => setEditTitle(e.target.value)}
                                 placeholder="Title (optional)"
                                 className="text-xs font-semibold bg-background border border-border rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent flex-1 max-w-xs"
                             />
                         ) : (
                             snippet.title && <span className="text-xs font-semibold">{snippet.title}</span>
                         )}
                         {!isEditing && (
                             <>
                                 <span className="text-[10px] text-muted-foreground">{format(new Date(snippet.created_at), 'MMM d, h:mm a')}</span>
                                 <span className="text-[10px] text-muted-foreground">•</span>
                                 <span className="text-[10px] text-muted-foreground/80">{formatCharCount(snippet.content.length)}</span>
                             </>
                         )}
                     </div>
                     <div className={cn("flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity", isPreview && "!opacity-100")}>

                        {/* Preview Toggle Button */}
                        <button
                            onClick={() => { setIsPreview(v => !v); togglePreviewMode(snippet.id); }}
                            className={cn(
                                "p-1.5 rounded-md transition-colors",
                                isPreview
                                    ? "text-blue-600 bg-blue-50 dark:bg-blue-950/30 hover:bg-blue-100 dark:hover:bg-blue-950/50"
                                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                            title={isPreview ? "Show plain text" : "Preview as Markdown"}
                            disabled={isEditing}
                        >
                            {isPreview ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>

                        {/* Delete Button */}
                        <button
                            onClick={() => deleteSnippet(snippet.id, (noteId) => {
                                updateNote(noteId, { updated_at: new Date().toISOString() });
                            })}
                            className="p-1.5 rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                            title="Delete"
                            disabled={isEditing}
                        >
                            <Trash2 size={14} />
                        </button>

                        {/* Edit Button */}
                        {!isEditing && (
                            <button
                                onClick={handleEdit}
                                className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                                title="Edit"
                                disabled={isSelected}
                            >
                                <Pencil size={14} />
                            </button>
                        )}

                         {/* Downvote Button */}
                         <button
                            onClick={() => downvoteSnippet(snippet.id)}
                            className={cn(
                                "p-1.5 rounded-md hover:bg-accent transition-colors",
                                snippet.downvotes > 0 ? "text-red-600 bg-red-50 dark:bg-red-950/30" : "text-muted-foreground hover:text-red-600"
                            )}
                            title="Downvote"
                            disabled={isEditing}
                         >
                             <ThumbsDown size={14} fill={snippet.downvotes > 0 ? "currentColor" : "none"} />
                        </button>


                        {/* Upvote Button */}
                        <button
                            onClick={() => upvoteSnippet(snippet.id)}
                            className={cn(
                                "p-1.5 rounded-md hover:bg-accent transition-colors",
                                snippet.upvotes > 0 ? "text-green-600 bg-green-50 dark:bg-green-950/30" : "text-muted-foreground hover:text-green-600"
                            )}
                            title="Upvote"
                            disabled={isEditing}
                        >
                            <ThumbsUp size={14} fill={snippet.upvotes > 0 ? "currentColor" : "none"} />
                        </button>

                         {/* Favorite Button */}
                         <button
                            onClick={() => toggleFavorite(snippet.id)}
                            className={cn(
                                "p-1.5 rounded-md hover:bg-accent transition-colors",
                                snippet.favorite ? "text-pink-600" : "text-muted-foreground hover:text-pink-600"
                            )}
                            title="Favorite"
                            disabled={isEditing}
                         >
                             <Heart size={14} fill={snippet.favorite ? "currentColor" : "none"} />
                         </button>

                        {/* Copy Button */}
                        <button
                            onClick={copyToClipboard}
                            className={cn(
                                "p-1.5 rounded-md transition-all duration-200 flex items-center gap-1",
                                isCopied
                                    ? "text-green-600 bg-green-50 dark:bg-green-950/30"
                                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                            )}
                            title={isCopied ? "Copied!" : `Copy${snippet.copy_count > 0 ? ` (copied ${snippet.copy_count} time${snippet.copy_count !== 1 ? 's' : ''})` : ''}`}
                            disabled={isEditing}
                        >
                            {isCopied ? <Check size={14} /> : <Copy size={14} />}
                            {snippet.copy_count > 0 && !isCopied && (
                              <span className="text-[10px] font-mono opacity-60">{snippet.copy_count}</span>
                            )}
                        </button>
                     </div>
                 </div>

                 {/* Content */}
                 <div className="relative">
                     {isEditing ? (
                         <textarea
                             ref={contentTextareaRef}
                             value={editContent}
                             onChange={(e) => setEditContent(e.target.value)}
                             className="w-full p-3 text-sm whitespace-pre-wrap font-sans leading-relaxed text-foreground bg-background border-0 focus:outline-none resize-none min-h-[120px]"
                         />
                     ) : isPreview ? (
                         <MarkdownPreview
                             content={snippet.content}
                             className={cn(
                                 "p-3 transition-all duration-300",
                                 !isExpanded && needsExpand && "max-h-[200px] overflow-hidden"
                             )}
                         />
                     ) : (
                         <div
                             className={cn(
                                 "p-3 text-sm whitespace-pre-wrap font-sans leading-relaxed text-foreground/90 transition-all duration-300",
                                 !isExpanded && needsExpand && "max-h-[200px] overflow-hidden"
                             )}
                         >
                             {snippet.content}
                         </div>
                     )}

                     {/* Gradient Overlay when collapsed */}
                     {!isEditing && !isExpanded && needsExpand && (
                         <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-card to-transparent pointer-events-none" />
                     )}

                     {/* Image Display - View Mode */}
                     {!isEditing && snippetImages.length > 0 && (
                         <div className="px-3 pb-3 border-t border-border/30">
                             <ImagePreview
                                 images={snippetImages}
                                 onClick={handleImageClick}
                                 editable={false}
                                 size="sm"
                             />
                         </div>
                     )}

                     {/* Image Display - Edit Mode */}
                     {isEditing && (
                         <div className="px-3 pb-2 border-t border-border/30">
                             <ImagePreview
                                 images={[...editImages, ...pendingImages]}
                                 onRemove={handleRemoveEditImage}
                                 onAdd={addImages}
                                 editable={true}
                                 canAddMore={totalEditImages < 10}
                                 size="sm"
                             />
                         </div>
                     )}

                     {/* Save/Cancel Buttons in Edit Mode */}
                     {isEditing && (
                         <div className="flex flex-col items-stretch gap-2 px-3 py-2 border-t border-border/50 bg-muted/20">
                             {/* Buttons row */}
                            <div className="flex items-center justify-end gap-2">
                                <span className="text-[10px] text-muted-foreground">
                                     Press <kbd className="px-1 py-0.5 text-[9px] bg-muted border border-border rounded">ESC</kbd> to cancel, <kbd className="px-1 py-0.5 text-[9px] bg-muted border border-border rounded">⌘+Enter</kbd> to save
                                 </span>
                                 <button
                                     onClick={handleCancel}
                                     className="px-3 py-1.5 text-xs font-medium rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors flex items-center gap-1"
                                 >
                                     <X size={12} />
                                     Cancel
                                 </button>
                                 <button
                                     onClick={handleSave}
                                     className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-1"
                                 >
                                     <Check size={12} />
                                     Save
                                 </button>
                             </div>
                         </div>
                     )}

                     {/* Expand/Collapse Button */}
                     {!isEditing && needsExpand && (
                         <button
                             onClick={() => setIsExpanded(!isExpanded)}
                             className="w-full py-2 text-xs font-medium text-primary hover:text-primary/80 hover:bg-accent/50 transition-colors flex items-center justify-center gap-1 border-t border-border/50"
                         >
                             {isExpanded ? (
                                 <>
                                     <ChevronDown size={14} className="rotate-180 transition-transform" />
                                     Show less
                                 </>
                             ) : (
                                 <>
                                     <ChevronDown size={14} className="transition-transform" />
                                     Click to expand
                                 </>
                             )}
                         </button>
                     )}
                 </div>
             </div>

             {/* Sticky "Show Less" Button - Appears at bottom of viewport when expanded content is long */}
             {showStickyCollapse && isExpanded && needsExpand && (
                <div className="fixed bottom-[calc(5rem+var(--sab))] left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-2">
                    <button
                        onClick={() => setIsExpanded(false)}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-full shadow-lg hover:bg-primary/90 transition-all flex items-center gap-2 text-sm font-medium"
                    >
                        <ChevronDown size={16} className="rotate-180" />
                        Show less
                    </button>
                </div>
             )}

             {/* Image Lightbox */}
             {lightboxOpen && snippetImages.length > 0 && (
                 <ImageLightbox
                     images={snippetImages}
                     currentIndex={lightboxIndex}
                     onIndexChange={setLightboxIndex}
                     onClose={() => setLightboxOpen(false)}
                 />
             )}
        </div>
    );
}
